import { useState, useEffect, useRef } from "react";
import { Popper, Paper, Grid, Divider } from "@mzc-pdc/ui";
import theme from "@styles/theme";

const AdaptivePopper = ({
    open,
    anchorEl,
    placement,
    disablePortal = false,
    minHeight = 150,
    maxHeight = 600,
    headerComponent,
    children,
    footerComponent,
    wrapperSx = {},
    onClose,
    ...popperProps
}) => {
    const popperRef = useRef(null);
    const [popperHeight, setPopperHeight] = useState(maxHeight);
    const [dynamicPlacement, setDynamicPlacement] = useState(placement);

    // 뷰포트 크기 변경 감지 및 높이 조정
    useEffect(() => {
        const updatePopperHeight = () => {
            if (!anchorEl || !open) return;

            setTimeout(() => {
                if (!popperRef.current) return;

                const anchorRect = anchorEl.getBoundingClientRect();
                let availableHeight;

                if (dynamicPlacement?.startsWith("top")) {
                    availableHeight = anchorRect.top - 16; // 상단 여백
                } else {
                    availableHeight = window.innerHeight - anchorRect.bottom - 16; // 하단 여백
                }

                const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, availableHeight));
                setPopperHeight(calculatedHeight);
            }, 0);
        };

        const calculatePlacement = () => {
            if (!placement && anchorEl && open) {
                const anchorRect = anchorEl.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                const anchorCenterY = anchorRect.top + anchorRect.height / 2;

                // 브라우저 크기 절반보다 위에 있으면 bottom-end, 아래에 있으면 top-end
                if (anchorCenterY < windowHeight / 2) {
                    setDynamicPlacement("bottom-end");
                } else {
                    setDynamicPlacement("top-end");
                }
            } else if (placement) {
                setDynamicPlacement(placement);
            }
        };

        if (open) {
            updatePopperHeight();
            calculatePlacement();
            window.addEventListener("resize", updatePopperHeight);

            let observer;
            if (popperRef.current) {
                observer = new MutationObserver(() => {
                    updatePopperHeight();
                });
                observer.observe(popperRef.current, {
                    attributes: true,
                    attributeFilter: ["data-popper-placement", "style"],
                });
            }

            return () => {
                window.removeEventListener("resize", updatePopperHeight);
                if (observer) observer.disconnect();
            };
        }
    }, [open, dynamicPlacement]);

    if (open !== undefined && !open) return null;

    return (
        <Popper
            ref={popperRef}
            anchorEl={anchorEl}
            placement={dynamicPlacement}
            open={open}
            disablePortal={disablePortal}
            modifiers={[
                {
                    name: "flip",
                    enabled: false,
                },
            ]}
            sx={{
                borderRadius: "4px",
                border: `solid 1px ${theme.palette.greySecondary[100]}`,
                boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.16)",
                zIndex: 1499,
            }}
            {...popperProps}
        >
            <Paper
                sx={{
                    height: `${popperHeight}px`,
                    minHeight: `${minHeight}px`,
                    maxHeight: `${maxHeight}px`,
                    overflowX: "hidden",
                    overflowY: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    "& .MuiTreeItem-content": {
                        borderTopRightRadius: "0 !important",
                        borderBottomRightRadius: "0 !important",
                    },
                    ...wrapperSx,
                }}
            >
                <Grid container flexDirection="column" sx={{ height: "inherit", flex: 1 }}>
                    {headerComponent}

                    {/* 본문 영역 - 크기 조정 시 이 영역만 축소/확대 */}
                    <Grid
                        item
                        flex={1}
                        className="tree-view-wrapper"
                        sx={{
                            overflowX: "auto",
                            overflowY: "auto",
                            minHeight: 0, // flex 아이템의 최소 크기를 0으로 설정하여 축소 가능하게 함
                        }}
                    >
                        {children}
                    </Grid>

                    {/* 푸터 영역 */}
                    {footerComponent && (
                        <>
                            <Grid item sx={{ flexShrink: 0 }}>
                                <Divider flexItem />
                            </Grid>
                            <Grid item sx={{ flexShrink: 0 }}>
                                {footerComponent}
                            </Grid>
                        </>
                    )}
                </Grid>
            </Paper>
        </Popper>
    );
};

export default AdaptivePopper;
